import React, { useState } from 'react';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { WorkOrder, workOrderPriorityColors, WorkOrderPriority, getAssignees, getDelegations } from '../../../types/workOrders';
import { translateWorkOrderPriority } from '../../../types/EnumTranslations';
import { formatDateForDisplay } from '../../../Helpers/DateUtils';

/**
 * Compact assignment label for a work-order row: one name, or "N tilldelade"
 * for several. Counts internal assignees + external delegates (normalized).
 */
export function workOrderAssignmentSummary(wo: WorkOrder, getUserName: (uid: string) => string): string | null {
    const uids = [...Object.keys(getAssignees(wo)), ...Object.keys(getDelegations(wo))];
    if (uids.length === 0) return null;
    if (uids.length === 1) return getUserName(uids[0]);
    return `${uids.length} tilldelade`;
}

interface LinkWorkOrdersModalProps {
    show: boolean;
    onHide: () => void;
    /** Open work orders of the project's building that belong to no project. */
    unlinkedWorkOrders: WorkOrder[];
    /** Links the chosen work orders to the project; throws on failure. */
    onLink: (workOrderKeys: string[]) => Promise<void>;
    getUserName: (uid: string) => string;
}

/**
 * "Koppla ärende": pick loose work orders of the same building and link them
 * to a project as delmoment. Shared by the project board and the project page.
 */
const LinkWorkOrdersModal: React.FC<LinkWorkOrdersModalProps> = ({
    show,
    onHide,
    unlinkedWorkOrders,
    onLink,
    getUserName
}) => {
    const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
    const [linking, setLinking] = useState(false);
    const [linkError, setLinkError] = useState<string | null>(null);

    const handleClose = () => {
        setSelectedKeys([]);
        setLinkError(null);
        onHide();
    };

    const handleLinkSelected = async () => {
        setLinking(true);
        setLinkError(null);
        try {
            await onLink(selectedKeys);
            setSelectedKeys([]);
            onHide();
        } catch (err) {
            console.error('Failed to link work orders:', err);
            setLinkError(err instanceof Error ? err.message : 'Kunde inte koppla ärenden');
        } finally {
            setLinking(false);
        }
    };

    const toggleSelection = (key: string) => {
        setSelectedKeys(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const priorityBadge = (priority: WorkOrderPriority) => (
        <Badge bg={workOrderPriorityColors[priority]} text={priority === WorkOrderPriority.MEDIUM ? 'dark' : undefined}>
            {translateWorkOrderPriority(priority)}
        </Badge>
    );

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Koppla ärende till delmoment</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {linkError && (
                    <Alert variant="danger" dismissible onClose={() => setLinkError(null)} className="mb-3">
                        {linkError}
                    </Alert>
                )}
                {unlinkedWorkOrders.length === 0 ? (
                    <p className="text-muted mb-0">
                        Inga olänkade ärenden finns för denna byggnad.
                    </p>
                ) : (
                    <>
                        <p className="text-muted mb-3">
                            Välj ärenden att koppla till detta projekt:
                        </p>
                        <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                            {unlinkedWorkOrders.map(wo => {
                                const isSelected = selectedKeys.includes(wo.key);
                                const assignment = workOrderAssignmentSummary(wo, getUserName);
                                return (
                                    <div
                                        key={wo.key}
                                        className={`link-work-order-row${isSelected ? ' link-work-order-row--selected' : ''}`}
                                        onClick={() => toggleSelection(wo.key)}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            checked={isSelected}
                                            onChange={() => toggleSelection(wo.key)}
                                            onClick={(e) => e.stopPropagation()}
                                            aria-label={`Välj ${wo.title}`}
                                            className="me-3"
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div className="fw-semibold">{wo.title}</div>
                                            <div className="small text-muted">
                                                {wo.dueDate && formatDateForDisplay(wo.dueDate)}
                                                {assignment && ` • ${assignment}`}
                                            </div>
                                        </div>
                                        {priorityBadge(wo.priority)}
                                    </div>
                                );
                            })}
                        </div>
                    </>
                )}
            </Modal.Body>
            <Modal.Footer>
                {unlinkedWorkOrders.length === 0 ? (
                    <Button variant="secondary" onClick={handleClose}>Stäng</Button>
                ) : (
                    <>
                        <Button variant="secondary" onClick={handleClose}>Avbryt</Button>
                        <Button
                            variant="primary"
                            onClick={handleLinkSelected}
                            disabled={selectedKeys.length === 0 || linking}
                        >
                            {linking ? <Spinner size="sm" /> : `Lägg till (${selectedKeys.length})`}
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
};

export default LinkWorkOrdersModal;
